import type { CommunityNotice } from '@shared/types';
import type { RanchAlert } from '@shared/ranch-feed';

/**
 * Compact Home alerts — critical only, one line each.
 *
 * Home used to render full cards (body, resilience copy, three action
 * buttons) below search. Those belong on /prepare, /emergency, and
 * What's On. Home only surfaces a title when something is actually
 * urgent, and it stays inside the hero → search stack.
 */

export type HomeAlert = {
  id: string;
  title: string;
  href?: string;
  external?: boolean;
};

export function pickCriticalHomeAlerts(input: {
  weather: { severeAlert?: string | null } | null;
  ranchAlerts: RanchAlert[];
  notices: CommunityNotice[];
}): HomeAlert[] {
  const out: HomeAlert[] = [];
  const seen = new Set<string>();

  const push = (alert: HomeAlert) => {
    const key = alert.title.trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(alert);
  };

  const nws = (input.weather?.severeAlert ?? '').trim();
  if (nws) {
    const lower = nws.toLowerCase();
    const hurricane = lower.includes('hurricane') || lower.includes('tropical');
    push({
      id: 'nws',
      title: hurricane ? 'Hurricane / tropical alert active' : 'Weather alert',
      href: hurricane ? '/prepare' : undefined,
    });
  }

  for (const a of input.ranchAlerts) {
    push({
      id: `ranch-${a.id}`,
      title: a.title,
      href: a.url ?? undefined,
      external: Boolean(a.url),
    });
  }

  for (const n of input.notices) {
    if (n.severity !== 'alert') continue;
    push({
      id: `notice-${n.id}`,
      title: n.title,
      href: '/events?filter=week',
    });
  }

  return out;
}
